import React, { useState, useEffect } from 'react'
import { ListGroup, Tabs, Tab } from 'react-bootstrap'
import styled from 'styled-components'
import { Input } from 'components'
import { getIconColor, svgMarker, pngMarker } from './utils'
import { useEnum } from 'hooks/useEnum'
import { kebabCase } from 'lodash'
import grayMarker from 'images/markers/gray.png'
import classnames from 'classnames'

const LeftPanel = ({
  organizations,
  onSelect,
  onFilter,
  onTypeChange,
  onReset,
  onShowNonMembers,
  showNonMembers,
  orgName,
  onClear,
}) => {
  const [selectedTypes, setSelectedTypes] = useState([])

  const { schemas: enumSchemas, getEnum } = useEnum()

  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    setSearchTerm(orgName)
    onFilter(orgName)
  }, [orgName])

  const handleOnInputChange = (e) => {
    const searchTerm = e.target.value
    setSearchTerm(searchTerm)
    onFilter(searchTerm)
  }

  const pngMaker = (type) => {
    return <img src={pngMarker(type)} alt="Marker" width={24} />
  }

  useEffect(() => {
    getEnum()
  }, [])

  useEffect(() => {
    if (enumSchemas) {
      const types = enumSchemas?.organization?.organizationType?.map(
        ({ elementValue }) => elementValue
      )
      setSelectedTypes(types)
    }
  }, [enumSchemas])

  const handleOnChange = (elementValue) => {
    let selectedResult = []
    if (selectedTypes.includes(elementValue)) {
      selectedResult = selectedTypes.filter((type) => type !== elementValue)
    } else {
      selectedResult = [...selectedTypes, elementValue]
    }
    setSelectedTypes(selectedResult)
    onTypeChange(selectedResult)
  }

  const handleCheckAll = (e) => {
    if (e.target.checked) {
      const allTypes = enumSchemas?.organization?.organizationType?.map(
        ({ elementValue }) => elementValue
      )
      setSelectedTypes(allTypes)
      onTypeChange(allTypes)
    } else {
      setSelectedTypes([])
      onTypeChange([])
    }
  }

  const handleReset = () => {
    const allTypes = enumSchemas?.organization?.organizationType?.map(
      ({ elementValue }) => elementValue
    )
    setSelectedTypes(allTypes)
    onTypeChange(allTypes)
    onReset()
  }

  return (
    <StyledTabs
      defaultActiveKey="org"
      id="uncontrolled-tab-example"
      onSelect={() => handleReset()}
    >
      <Tab eventKey="org" title="Name">
        <StyledInput
          placeholder="Search Organization"
          onChange={handleOnInputChange}
          className="mt-3 mb-1"
          value={searchTerm}
        />
        <div className="text-center mt-3 mb-2">
          <Button onClick={onClear}>
            Clear filter
          </Button>
        </div>
        <StyledListGroup>
          <NonMemberListItem className="px-0">
            <NonMemberLabel className="mb-0">
              <input
                type="checkbox"
                name="showNonMembers"
                className="mr-1"
                onChange={() => onShowNonMembers(!showNonMembers)}
                checked={showNonMembers}
              />
              <span>Show non-members</span>
            </NonMemberLabel>
            <NonMemberImage src={grayMarker} alt="Non-member" />
          </NonMemberListItem>
          {organizations.map((organization, index) => (
            <ListItem
              key={index}
              onClick={() => onSelect(organization)}
              className={classnames({
                'd-none': !organization.isNspaMember && !showNonMembers,
              })}
            >
              <IconWrapper>
                {pngMaker(
                  organization.isNspaMember ? organization.type : 'Nonmember'
                )}
              </IconWrapper>
              <span>{organization.name}</span>
            </ListItem>
          ))}
        </StyledListGroup>
      </Tab>
      <Tab eventKey="type" title="Types">
        <StyledListGroup className="mt-3">
          <ListItem>
            <StyledLabel className="mb-0">
              <input
                type="checkbox"
                checked={
                  selectedTypes.length ===
                  enumSchemas?.organization?.organizationType?.length
                }
                onChange={handleCheckAll}
                className="mr-3"
              />
              <span>Select All</span>
            </StyledLabel>
          </ListItem>
          {enumSchemas?.organization?.organizationType &&
            enumSchemas?.organization?.organizationType?.map(
              ({ elementValue }) => (
                <ListItem key={kebabCase(elementValue)}>
                  <StyledLabel className="mb-0">
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(elementValue)}
                      onChange={() => handleOnChange(elementValue)}
                      className="mr-3"
                    />
                    <span>{elementValue}</span>
                  </StyledLabel>
                </ListItem>
              )
            )}
        </StyledListGroup>
      </Tab>
    </StyledTabs>
  )
}

const windowHeight = window.innerHeight
const height = windowHeight - 300

const StyledInput = styled(Input)`
  height: 2rem;
  font-size: 0.8rem;
`

const StyledListGroup = styled(ListGroup)`
  overflow-y: auto;
  height: ${height}px;
`

const StyledTabs = styled(Tabs)`
  width: 100%;

  > a {
    width: 50%;
    text-align: center;
    border-color: #fff !important;
    color: gray;
  }

  > .active {
    border-bottom: 2px solid ${({ theme }) => theme.palette.primary[0]} !important;
    color: ${({ theme }) => theme.palette.primary[0]} !important;
  }
`

const ListItem = styled(ListGroup.Item)`
  display: flex;
  align-items: center;
  justify-content: left;
  cursor: pointer;
`

const IconWrapper = styled.div`
  margin-right: 4px;

  > img {
    width: 24px;
  }
`

const StyledLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: left;
  cursor: pointer;
  gap: 10px;
  margin-bottom: 0;
`

const NonMemberLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: left;
  cursor: pointer;
  gap: 10px;
  margin-bottom: 0;
`

const NonMemberListItem = styled(ListGroup.Item)`
  border-top: none;
  border-left: none;
  border-right: none;
  /* padding: 0.5rem; */
`

const NonMemberImage = styled.img`
  width: 24px;
  position: absolute;
  right: 0;
  top: 9px;
`

const Button = styled.button`
  background-color: ${({ theme }) => theme.palette.primary[2]};
  border: 0;
  color: ${({ theme }) => theme.palette.grayscale[8]};
  font-size: 0.8rem;
  padding: 0.5rem 2rem;
  border-radius: 0.2rem;
  margin: 0 auto;

  &:disabled {
    background-color: ${({ theme }) => theme.palette.grayscale[5]};
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export default LeftPanel
